import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useButton } from '@react-aria/button';
import { useFocusRing } from '@react-aria/focus';
import omit from 'lodash/omit';
import Link from 'next/link';
import { useCallback, useRef } from 'react';
import { useTheme } from 'styled-components';
import { FontWeights } from '../../utils/typography';
import Loader from '../Loader';
import { ButtonIcon, Wrapper } from './styles';
import { FocusRingVariants, } from './types';
export { FocusRingVariants, ButtonSizes } from './types';
export const OptionalBackgroundButton = (_a) => {
    var _b;
    var { backgroundColor, borderColor, children, className, contentAlignment, Icon, iconColor, isDisabled, isLoading, maxWidth, legacyOnClick, onPress, onPressStart, textColor, fontWeight, type: _type, fullWidth, loaderColor, openInNewTab, tabIndex = 0, focusRingVariant = FocusRingVariants.Light, size: _size, hideShadow, buttonRef: _buttonRef, UNSAFE_showFocusRing } = _a, props = __rest(_a, ["backgroundColor", "borderColor", "children", "className", "contentAlignment", "Icon", "iconColor", "isDisabled", "isLoading", "maxWidth", "legacyOnClick", "onPress", "onPressStart", "textColor", "fontWeight", "type", "fullWidth", "loaderColor", "openInNewTab", "tabIndex", "focusRingVariant", "size", "hideShadow", "buttonRef", "UNSAFE_showFocusRing"]);
    // TODO: clean up / standardize ref handling
    const ref = useRef(null);
    const buttonRef = _buttonRef !== null && _buttonRef !== void 0 ? _buttonRef : ref;
    const theme = useTheme();
    const size = _size !== null && _size !== void 0 ? _size : theme.buttons.defaultSize;
    // Handle both useFocusRing and any custom focus callbacks
    const { isFocusVisible, focusProps } = useFocusRing({ within: true });
    const { onFocus: focusPropsOnFocus, onBlur: focusPropsOnBlur } = focusProps;
    const { onFocus: propsOnFocus, onBlur: propsOnBlur } = props;
    const onFocus = useCallback(e => {
        focusPropsOnFocus === null || focusPropsOnFocus === void 0 ? void 0 : focusPropsOnFocus(e);
        propsOnFocus === null || propsOnFocus === void 0 ? void 0 : propsOnFocus(e);
    }, [focusPropsOnFocus, propsOnFocus]);
    const onBlur = useCallback(e => {
        focusPropsOnBlur === null || focusPropsOnBlur === void 0 ? void 0 : focusPropsOnBlur(e);
        propsOnBlur === null || propsOnBlur === void 0 ? void 0 : propsOnBlur(e);
    }, [focusPropsOnBlur, propsOnBlur]);
    const isInternalLink = (_b = props.href) === null || _b === void 0 ? void 0 : _b.startsWith('/');
    const elementType = props.href ? (isInternalLink ? Link : 'a') : 'button';
    const type = elementType === 'a' || elementType === Link ? undefined : _type !== null && _type !== void 0 ? _type : 'button';
    const { buttonProps } = useButton(Object.assign({ isDisabled: isDisabled || isLoading, type,
        onPress,
        onPressStart, elementType: elementType === Link ? 'a' : elementType }, props), buttonRef);
    const allowedProps = (() => {
        // Fix issue where keyboard submission is preventDefault()ed.
        // New version of react-aria/button will fix this mid-Jan 2022.
        const submitSafeProps = type === 'submit'
            ? omit(buttonProps, [
                'onKeyUp',
                'onKeyDown',
                'onKeyPress',
                'onClick',
                'onKeyPressChange',
                'onKeyPressStart',
                'onKeyPressEnd',
            ])
            : buttonProps;
        // Removes role='button' from normal links.
        // That role is only meant for when it does not behave as a link
        return elementType === 'a' || elementType === Link ? omit(submitSafeProps, ['role']) : submitSafeProps;
    })();
    const { onMouseEnter: buttonPropsOnMouseEnter, onMouseLeave: buttonPropsOnMouseLeave } = allowedProps;
    const { onMouseEnter: propsOnMouseEnter, onMouseLeave: propsOnMouseLeave } = props;
    const onMouseEnter = useCallback(e => {
        buttonPropsOnMouseEnter === null || buttonPropsOnMouseEnter === void 0 ? void 0 : buttonPropsOnMouseEnter(e);
        propsOnMouseEnter === null || propsOnMouseEnter === void 0 ? void 0 : propsOnMouseEnter(e);
    }, [buttonPropsOnMouseEnter, propsOnMouseEnter]);
    const onMouseLeave = useCallback(e => {
        buttonPropsOnMouseLeave === null || buttonPropsOnMouseLeave === void 0 ? void 0 : buttonPropsOnMouseLeave(e);
        propsOnMouseLeave === null || propsOnMouseLeave === void 0 ? void 0 : propsOnMouseLeave(e);
    }, [buttonPropsOnMouseLeave, propsOnMouseLeave]);
    const iconOnly = Boolean(Icon && !isLoading && !children);
    return (_jsxs(Wrapper, Object.assign({ as: elementType, "data-testid": `${elementType === Link ? 'Link' : elementType}-element-type`, "$backgroundColor": backgroundColor, "$borderColor": borderColor, "$focusRingVariant": focusRingVariant, className: className, "$isDisabled": isDisabled }, props, allowedProps, focusProps, { target: elementType !== 'button' && openInNewTab ? '_blank' : undefined, type: type, "$isFocusVisible": Boolean(isFocusVisible || UNSAFE_showFocusRing), onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, onFocus: onFocus, onBlur: onBlur, "$contentAlignment": contentAlignment, "$textColor": textColor || 'white', "$fontWeight": fontWeight !== null && fontWeight !== void 0 ? fontWeight : FontWeights.Medium, "$fullWidth": fullWidth, "$maxWidth": maxWidth }, (legacyOnClick ? { onClick: legacyOnClick } : {}), { "$isLoading": isLoading, "$size": size, tabIndex: tabIndex, "$hasIconAndText": Boolean(Icon) && !iconOnly, "$hideShadow": hideShadow, ref: buttonRef, children: [!isLoading && Icon && (_jsx(ButtonIcon, { "$color": iconColor || textColor || 'white', "$isDisabled": isDisabled, "$iconOnly": iconOnly, "$size": size, children: _jsx(Icon, {}) })), children, isLoading ? _jsx(Loader, { color: loaderColor }) : null] })));
};
/**
 *
 * Displays a Button based on:
 * https://www.figma.com/file/RSwYY8r0WWLfGH04ky7sxX/Calm---Web?node-id=1600%3A0
 *
 * * optionally displays a svg icon on the button (`icon={<Twitter />}`)
 * * optionally left-aligns the button label
 * * optionally renders as an `<a>` tag if href is passed in
 * * optionally calls onPress if passed in as a prop
 *
 */
const Button = (props) => _jsx(OptionalBackgroundButton, Object.assign({}, props));
export const PrimaryButton = (props) => {
    var _a, _b;
    const theme = useTheme();
    return (_jsx(Button, Object.assign({ backgroundColor: "buttonPrimaryBg", textColor: "buttonPrimaryText", loaderColor: "buttonPrimaryLoader", hideShadow: (_a = theme.buttons) === null || _a === void 0 ? void 0 : _a.primaryHideShadow, focusRingVariant: (_b = theme.buttons) === null || _b === void 0 ? void 0 : _b.primaryFocusRingVariant }, props)));
};
export const SecondaryButton = (props) => {
    var _a, _b;
    const theme = useTheme();
    return (_jsx(Button, Object.assign({ backgroundColor: "buttonSecondaryBg", textColor: "buttonSecondaryText", loaderColor: "buttonSecondaryLoader", hideShadow: (_a = theme.buttons) === null || _a === void 0 ? void 0 : _a.secondaryHideShadow, focusRingVariant: (_b = theme.buttons) === null || _b === void 0 ? void 0 : _b.secondaryFocusRingVariant }, props)));
};
export default Button;
//# sourceMappingURL=index.js.map